/**
 * Shared composite bits for payments / bookings / helpdesk screens, built
 * only from src/ui primitives + tokens + this module's icon set (plus a
 * couple of read-only reuses of src/components/gate — InfoNote and the date
 * formatters — which is the established cross-reuse pattern in this
 * workspace; see src/features/account-admin/bits.tsx).
 */
import { router } from 'expo-router';
import React from 'react';
import { Pressable, ScrollView, StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { glow, formatINR, radius, space, type PillStatus } from '@bero/tokens';
import { useT, monthShort, monthLong, dayShort, dayLong } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { useTheme } from '@/theme';
import { AppText, Card, Pill, Skeleton } from '@/ui';

import { Icon, type IconName } from './icons';
import type { TicketCategory, VenueBookingStatus } from './api';

/** Back that survives cold web deep-links — falls back per screen family. */
export function backOr(fallback: '/(tabs)/pay' | '/(tabs)/home' | '/(tabs)/profile') {
  return () => { if (router.canGoBack()) router.back(); else router.replace(fallback); };
}

/* ---------------- date / time formatting ---------------- */
// Month/day names come from the i18n module (monthShort/monthLong/dayShort/dayLong)
// so these formatters localise with the active language.

function parseISODate(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** "2026-07" -> "July 2026" */
export function monthYear(period: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(period);
  if (!m) return period;
  const mi = Number(m[2]) - 1;
  return `${monthLong(mi) || period} ${m[1]}`;
}

/** "2026-08-15" -> "15 Aug" */
export function shortDate(iso: string): string {
  const d = parseISODate(iso);
  if (!d) return iso;
  return `${d.getDate()} ${monthShort(d.getMonth())}`;
}

/** "2026-08-15" -> "Saturday, 15 August" */
export function longDate(iso: string): string {
  const d = parseISODate(iso);
  if (!d) return iso;
  return `${dayLong(d.getDay())}, ${d.getDate()} ${monthLong(d.getMonth())}`;
}

/** For date strips: { weekday: 'Sat', day: '25' } */
export function dateParts(iso: string): { weekday: string; day: string } {
  const d = parseISODate(iso);
  if (!d) return { weekday: '', day: '' };
  return { weekday: dayShort(d.getDay()), day: String(d.getDate()) };
}

/** "18:00" -> "6:00 PM" */
export function fmt12h(hhmm: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!m) return hhmm;
  let h = Number(m[1]);
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${m[2]} ${ampm}`;
}

export function slotRangeLabel(start: string, end: string | null): string {
  return end ? `${fmt12h(start)} – ${fmt12h(end)}` : fmt12h(start);
}

/** Ultra-compact range for slot tiles: "6–8 AM", "10 AM–12 PM", "12–2 PM". */
export function slotRangeCompact(start: string, end: string | null): string {
  const parse = (t: string) => {
    const [hh] = t.split(':');
    let h = Number(hh);
    const am = h < 12;
    h = h % 12 || 12;
    return { h, am };
  };
  const a = parse(start);
  if (!end) return `${a.h} ${a.am ? 'AM' : 'PM'}`;
  const b = parse(end);
  if (a.am === b.am) return `${a.h}–${b.h} ${b.am ? 'AM' : 'PM'}`;
  return `${a.h} ${a.am ? 'AM' : 'PM'}–${b.h} ${b.am ? 'AM' : 'PM'}`;
}

/** ISO date (yyyy-mm-dd) for "today + n days", used to build booking date strips. */
export function isoDateAt(daysFromToday: number): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + daysFromToday);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** "Today, 3:42 PM" for a Date. */
export function fmtNow(d: Date): string {
  let h = d.getHours();
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `Today, ${h}:${String(d.getMinutes()).padStart(2, '0')} ${ampm}`;
}

/** "3 Jul, 2:41 PM" for a ticket timeline / comment timestamp. */
export function fmtStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  let h = d.getHours();
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${d.getDate()} ${monthShort(d.getMonth())}, ${h}:${String(d.getMinutes()).padStart(2, '0')} ${ampm}`;
}

/* ---------------- status → Pill mapping ---------------- */
// PAID maps to the 'approved' (success-tinted) pill status, not 'resolved' —
// "resolved" is helpdesk-ticket vocabulary and read as confusing on a
// payment (F4 P0-4). Pair with billPillLabel() at the call site so the
// rendered label reads "PAID", not the shared Pill's default "APPROVED".
export function billPillStatus(status: 'DUE' | 'PAID' | 'OVERDUE'): PillStatus {
  return status === 'PAID' ? 'approved' : status === 'OVERDUE' ? 'urgent' : 'pending';
}
// Only overrides the label for PAID (in scope per audit) — DUE/OVERDUE keep
// falling back to the shared Pill's default PENDING/URGENT labels, unchanged.
export function billPillLabel(status: 'DUE' | 'PAID' | 'OVERDUE'): string | undefined {
  return status === 'PAID' ? 'PAID' : undefined;
}
/** "INV-2026-07-0008" from a bill's period + numeric id (display-only). */
export function invoiceCode(period: string, id: number): string {
  return `INV-${period}-${String(id).padStart(4, '0')}`;
}

/**
 * formatINR wrapper for money-critical amounts stored in paise: shows the
 * paise component (decimals: 2) only when it's actually non-zero, so a
 * round-rupee amount stays "₹4,250" instead of a noisy "₹4,250.00" (F4 P1-6).
 */
export function inr(paise: number): string {
  const hasPaise = Math.round(paise) % 100 !== 0;
  return formatINR(paise / 100, hasPaise ? { decimals: 2 } : {});
}
export function venuePillStatus(status: VenueBookingStatus): PillStatus {
  return status === 'APPROVED' ? 'approved' : status === 'REJECTED' ? 'rejected' : 'pending';
}
export function ticketPillStatus(status: string): PillStatus {
  if (status === 'IN_PROGRESS') return 'progress';
  if (status === 'URGENT') return 'urgent';
  if (status === 'RESOLVED' || status === 'CLOSED') return 'resolved';
  return 'open';
}

/* ---------------- category / amenity icon + label maps ---------------- */
const CATEGORY_ICON: Record<TicketCategory, IconName> = {
  PLUMBING: 'droplet', ELECTRICAL: 'zap', SECURITY: 'shield', LIFT: 'arrow-up-circle',
  PEST: 'alert-octagon', CLEANING: 'wind', OTHER: 'more-horizontal',
};
const CATEGORY_LABEL: Record<TicketCategory, string> = {
  PLUMBING: 'Plumbing', ELECTRICAL: 'Electrical', SECURITY: 'Security', LIFT: 'Lift',
  PEST: 'Pest', CLEANING: 'Cleaning', OTHER: 'Other',
};
/** i18n keys for the ticket categories — resolved at render (categoryLabel below
 * stays for non-React callers / the EN fallback). */
export const CATEGORY_LABEL_KEY: Record<TicketCategory, DictKey> = {
  PLUMBING: 'hd_cat_plumbing', ELECTRICAL: 'hd_cat_electrical', SECURITY: 'hd_cat_security',
  LIFT: 'hd_cat_lift', PEST: 'hd_cat_pest', CLEANING: 'hd_cat_cleaning', OTHER: 'hd_cat_other',
};
export function categoryIcon(cat: TicketCategory): IconName { return CATEGORY_ICON[cat]; }
export function categoryLabel(cat: TicketCategory): string { return CATEGORY_LABEL[cat]; }
export const ALL_CATEGORIES: TicketCategory[] = ['PLUMBING', 'ELECTRICAL', 'SECURITY', 'LIFT', 'PEST', 'CLEANING', 'OTHER'];

export function amenityIcon(name: string): IconName {
  const n = name.toLowerCase();
  if (n.includes('gym')) return 'dumbbell';
  if (n.includes('pool') || n.includes('swim')) return 'waves';
  if (n.includes('tennis') || n.includes('court')) return 'target';
  return 'users';
}

const KNOWN_AMENITY_ICONS = new Set<string>(['dumbbell', 'waves', 'target', 'sparkles', 'users']);

/** Admins pick an icon key per amenity (app/blueprints/admin.py amenity endpoints);
 * fall back to the name-sniffing heuristic above for older/unset rows. */
export function resolvedAmenityIcon(a: { icon?: string; name: string }): IconName {
  return a.icon && KNOWN_AMENITY_ICONS.has(a.icon) ? (a.icon as IconName) : amenityIcon(a.name);
}
export function venueIcon(name: string): IconName {
  const n = name.toLowerCase();
  if (n.includes('rooftop') || n.includes('deck') || n.includes('party')) return 'sparkles';
  return 'users';
}

/* ---------------- FAB (56 circle, primary500, bottom-right 24) ---------------- */
export function Fab({ label, onPress }: { label: string; onPress: () => void }) {
  const { colors } = useTheme();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      style={({ pressed }) => ({
        position: 'absolute', right: 24, bottom: 24, width: 56, height: 56, borderRadius: radius.full,
        backgroundColor: pressed ? colors.primary600 : colors.primary500,
        alignItems: 'center', justifyContent: 'center', zIndex: 6,
        ...glow(colors.primary500, 0.4, 14),
      })}
    >
      <Icon name="plus" size={24} color={colors.canvas0} />
    </Pressable>
  );
}

/* ---------------- bottom action bar ---------------- */
export function BottomBar({ children, style }: { children: React.ReactNode; style?: StyleProp<ViewStyle> }) {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  return (
    <View
      style={[{
        borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: colors.borderSoft,
        backgroundColor: colors.canvas0,
        paddingHorizontal: space.s4, paddingTop: space.s3,
        paddingBottom: Math.max(insets.bottom, space.s3) + space.s2,
      }, style]}
    >
      {children}
    </View>
  );
}

/* ---------------- loading skeletons ---------------- */
export function CardSkeletons({ count = 3, h = 96 }: { count?: number; h?: number }) {
  return (
    <View style={{ gap: space.s3 }}>
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i}>
          <Skeleton w="35%" h={12} />
          <Skeleton w="80%" h={16} style={{ marginTop: space.s3 }} />
          <Skeleton w="55%" h={12} style={{ marginTop: space.s2 }} />
          <View style={{ height: Math.max(h - 88, 0) }} />
        </Card>
      ))}
    </View>
  );
}

/* ---------------- underline sub-tabs with counts ---------------- */
export function CountTabs<T extends string>({ tabs, value, onChange }: {
  tabs: ReadonlyArray<readonly [T, string]>; value: T; onChange: (v: T) => void;
}) {
  const { colors } = useTheme();
  return (
    <View style={{ flexDirection: 'row', borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.borderSoft }}>
      {tabs.map(([key, label]) => {
        const active = value === key;
        return (
          <Pressable
            key={key}
            accessibilityRole="tab"
            accessibilityState={{ selected: active }}
            onPress={() => onChange(key)}
            style={{ flex: 1, paddingVertical: space.s3, alignItems: 'center', minHeight: 44 }}
          >
            <AppText variant="bodyMd" weight={active ? '600' : '500'} tone={active ? 'primary' : 'tertiary'}>
              {label}
            </AppText>
            {active ? (
              <View style={{ position: 'absolute', bottom: 0, width: '55%', height: 2, borderRadius: 2, backgroundColor: colors.primary500 }} />
            ) : null}
          </Pressable>
        );
      })}
    </View>
  );
}

/* ---------------- category chip (helpdesk ticket cards) ---------------- */
export function CategoryChip({ category }: { category: TicketCategory }) {
  const { colors } = useTheme();
  const { t } = useT();
  return (
    <View style={{
      flexDirection: 'row', alignItems: 'center', gap: 5,
      backgroundColor: colors.canvas1, borderWidth: StyleSheet.hairlineWidth, borderColor: colors.borderSoft,
      borderRadius: 10, paddingHorizontal: space.s2, paddingVertical: 3, alignSelf: 'flex-start',
    }}>
      <Icon name={categoryIcon(category)} size={12} color={colors.textSecondary} />
      <AppText variant="label" style={{ color: colors.textSecondary }}>{t(CATEGORY_LABEL_KEY[category])}</AppText>
    </View>
  );
}

/* ---------------- amenity slot row ---------------- */
export function SlotRow({ label, sublabel, booked, onBook }: {
  label: string; sublabel: string; booked: boolean; onBook?: () => void;
}) {
  const { colors } = useTheme();
  const { t } = useT();
  return (
    <View style={{
      flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
      paddingVertical: 14, paddingHorizontal: space.s4, borderRadius: radius.lg,
      backgroundColor: booked ? colors.canvas1 : colors.canvas0,
      borderWidth: booked ? 0 : StyleSheet.hairlineWidth, borderColor: colors.borderSoft,
    }}>
      <View>
        <AppText variant="headingSm" weight="500" tone={booked ? 'tertiary' : 'primary'}>{label}</AppText>
        <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>{sublabel}</AppText>
      </View>
      {booked ? (
        <AppText variant="bodySm" tone="tertiary">{t('svc_booked')}</AppText>
      ) : (
        <Pressable
          accessibilityRole="button"
          onPress={onBook}
          style={({ pressed }) => ({
            minHeight: 36, paddingHorizontal: space.s3, borderRadius: radius.md,
            borderWidth: 1, borderColor: colors.primary500, alignItems: 'center', justifyContent: 'center',
            backgroundColor: pressed ? colors.primary50 : 'transparent',
          })}
        >
          <AppText variant="bodySm" weight="500" tone="brand">{t('qa_book')}</AppText>
        </Pressable>
      )}
    </View>
  );
}

/* ---------------- date strip chip (venue booking calendar) ---------------- */
export function DateChip({ weekday, day, selected, disabled, onPress }: {
  weekday: string; day: string; selected: boolean; disabled: boolean; onPress: () => void;
}) {
  const { colors } = useTheme();
  const { t } = useT();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected, disabled }}
      disabled={disabled}
      onPress={onPress}
      style={{
        width: 56, height: 72, borderRadius: radius.lg, flexShrink: 0,
        alignItems: 'center', justifyContent: 'center', gap: 3,
        borderWidth: selected ? 0 : 1, borderColor: colors.borderFirm,
        backgroundColor: selected ? colors.primary500 : 'transparent',
        opacity: disabled ? 0.4 : 1,
      }}
    >
      <AppText variant="label" style={{ color: selected ? colors.textOnDark : colors.textTertiary, letterSpacing: 0 }}>{weekday}</AppText>
      <AppText weight="500" style={{ fontSize: 18, lineHeight: 22, color: selected ? colors.textOnDark : colors.textPrimary }}>{day}</AppText>
      {disabled ? <AppText style={{ fontSize: 9, color: colors.textTertiary }}>{t('dc_busy')}</AppText> : null}
    </Pressable>
  );
}

export function ChipsRow({ children }: { children: React.ReactNode }) {
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{ gap: space.s2, paddingHorizontal: space.s4 }}
    >
      {children}
    </ScrollView>
  );
}

/* ---------------- dashed "add photo" placeholder (not wired — no upload API) ---------------- */
export function AddPhotoBox({ size = 80 }: { size?: number }) {
  const { colors } = useTheme();
  return (
    <View
      style={{
        width: size, height: size, borderRadius: radius.md, borderWidth: 1.5, borderStyle: 'dashed',
        borderColor: colors.borderFirm, backgroundColor: colors.canvas1,
        alignItems: 'center', justifyContent: 'center',
      }}
    >
      <Icon name="plus" size={22} color={colors.textTertiary} />
    </View>
  );
}

/* ---------------- vertical timeline entry ---------------- */
export function TimelineRow({ title, time, note, done, isLast }: {
  title: string; time: string; note?: string; done: boolean; isLast: boolean;
}) {
  const { colors } = useTheme();
  return (
    <View style={{ flexDirection: 'row', gap: space.s3 }}>
      <View style={{ width: 16, alignItems: 'center' }}>
        <View style={{
          width: 10, height: 10, borderRadius: 5, marginTop: 5,
          backgroundColor: done ? colors.primary500 : colors.canvas2,
          borderWidth: done ? 0 : 1.5, borderColor: colors.borderFirm,
        }} />
        {!isLast ? <View style={{ flex: 1, width: StyleSheet.hairlineWidth, backgroundColor: colors.borderSoft, marginTop: 2 }} /> : null}
      </View>
      <View style={{ flex: 1, paddingBottom: 18 }}>
        <AppText variant="bodyMd" weight="500">{title}</AppText>
        <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>{time}</AppText>
        {note ? (
          <View style={{ backgroundColor: colors.canvas1, borderRadius: radius.md, padding: space.s3, marginTop: space.s2 }}>
            <AppText variant="bodySm" tone="secondary" style={{ fontStyle: 'italic' }}>{note}</AppText>
          </View>
        ) : null}
      </View>
    </View>
  );
}

/* ---------------- kv row (info cards) ---------------- */
export function KVRow({ k, v, mono }: { k: string; v: React.ReactNode; mono?: boolean }) {
  return (
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 6 }}>
      <AppText variant="bodySm" tone="tertiary">{k}</AppText>
      {typeof v === 'string' ? <AppText variant="bodyMd" weight="500" mono={mono}>{v}</AppText> : v}
    </View>
  );
}
